import { Badge, Box, Button, FormField, Multiselect, SpaceBetween, Textarea } from '@cloudscape-design/components';
import { grantableFields, needsReason, type SourceDetail } from './types';

interface Props {
  source: SourceDetail;
  selected: string[];
  onChange: (fieldIds: string[]) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
}

/**
 * Pick which of a source's fields an app gets. Only active, classified fields can be
 * chosen: hidden ones must be classified first on the Data page (Systems tab). Restricted
 * fields are marked, and choosing one asks for the reason that goes in the ledger.
 */
export default function GrantFieldsEditor({ source, selected, onChange, reason, onReasonChange }: Props) {
  const fields = grantableFields(source);
  const standardIds = fields.filter((f) => f.class === 'standard').map((f) => f.fieldId);
  const reasonRequired = needsReason(source, selected);

  return (
    <SpaceBetween size="s">
      <SpaceBetween direction="horizontal" size="xs">
        <Box variant="h4">{source.displayName}</Box>
        {source.defaultClass === 'restricted' ? <Badge color="red">Restricted</Badge> : <Badge color="green">Standard</Badge>}
      </SpaceBetween>
      <FormField label="Fields" description={`${selected.length} of ${fields.length} selected`}>
        <SpaceBetween size="xs">
          <Multiselect
            selectedOptions={fields
              .filter((f) => selected.includes(f.fieldId))
              .map((f) => ({ value: f.fieldId, label: f.name }))}
            options={fields.map((f) => ({
              value: f.fieldId,
              label: f.name,
              description: f.type,
              ...(f.class === 'restricted' ? { tags: ['Restricted'] } : {}),
            }))}
            onChange={({ detail }) => onChange(detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v))}
            placeholder="Choose fields"
            filteringType="auto"
            tokenLimit={6}
            keepOpen
            ariaLabel={`Fields for ${source.displayName}`}
          />
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => onChange(standardIds)} disabled={standardIds.length === 0}>
              Select all standard fields
            </Button>
            <Button onClick={() => onChange([])} disabled={selected.length === 0}>
              Clear
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      </FormField>
      {reasonRequired && (
        <FormField
          label="Why does this app need restricted fields?"
          description="Required. Kept in the ledger alongside the grant."
          errorText={reason.trim() ? undefined : 'A reason is required to grant restricted fields.'}
        >
          <Textarea value={reason} onChange={({ detail }) => onReasonChange(detail.value)} rows={2} ariaLabel={`Reason for ${source.displayName}`} />
        </FormField>
      )}
    </SpaceBetween>
  );
}
